import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sends people back to the page they came from after signing in or signing up.
 * Every page visit (except /auth) is remembered, so any "sign in" link or
 * redirect to /auth returns to that page — e.g. a Fest Lead opening
 * /affiliate-dashboard from an email lands on the dashboard after login,
 * not on the homepage.
 */
const KEY = 'nf_return_to';

/** Only same-site paths, never /auth itself (no open redirects). */
export const safeReturnPath = (p?: string | null): string | null => {
  if (!p || !p.startsWith('/') || p.startsWith('//')) return null;
  if (p === '/auth' || p.startsWith('/auth?') || p.startsWith('/auth/')) return null;
  return p;
};

export const getReturnPath = (explicit?: string | null): string => {
  const fromParam = safeReturnPath(explicit);
  if (fromParam) return fromParam;
  try {
    return safeReturnPath(sessionStorage.getItem(KEY)) || '/';
  } catch {
    return '/';
  }
};

export const clearReturnPath = () => {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
};

/** Mounted once inside the router: remembers the last non-auth page. */
export function ReturnPathTracker() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    if (safeReturnPath(path)) {
      try { sessionStorage.setItem(KEY, path); } catch { /* ignore */ }
    }
  }, [location.pathname, location.search]);
  return null;
}
